import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { BellRing } from "lucide-react";
import { toast } from "sonner";

const SEV_LABEL = { info: "Info", warning: "Advertencia", critical: "Crítica" };

export default function UserNotificationsDialog({ open, onOpenChange, user, onSaved }) {
  const [prefs, setPrefs] = useState(null);
  const [properties, setProperties] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user || !open) return;
    setPrefs({
      email_notifications_enabled: user.email_notifications_enabled ?? true,
      notification_scope: user.notification_scope ?? "all",
      notification_property_ids: user.notification_property_ids ?? [],
      notification_severities: user.notification_severities ?? ["warning", "critical"],
    });
    api.get("/properties").then((r) => setProperties(r.data));
  }, [user, open]);

  if (!prefs || !user) return null;

  const toggleSev = (s) => {
    const cur = new Set(prefs.notification_severities);
    if (cur.has(s)) cur.delete(s); else cur.add(s);
    setPrefs({ ...prefs, notification_severities: Array.from(cur) });
  };

  const togglePropId = (pid) => {
    const cur = new Set(prefs.notification_property_ids);
    if (cur.has(pid)) cur.delete(pid); else cur.add(pid);
    setPrefs({ ...prefs, notification_property_ids: Array.from(cur) });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/users/${user.id}`, prefs);
      toast.success(`Preferencias de ${user.name} guardadas`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="user-notifications-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <BellRing className="w-5 h-5" /> Notificaciones — {user.name}
          </DialogTitle>
          <DialogDescription>
            Configurá qué alertas por email recibe <strong>{user.email}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border border-border/60 bg-card p-3">
            <div>
              <div className="text-sm font-medium">Recibir alertas por email</div>
              <div className="text-xs text-muted-foreground">Master switch. Si está apagado, no recibe nada.</div>
            </div>
            <Switch
              checked={prefs.email_notifications_enabled}
              onCheckedChange={(v) => setPrefs({ ...prefs, email_notifications_enabled: v })}
            />
          </div>

          {prefs.email_notifications_enabled && (
            <>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Alcance</div>
                <Select
                  value={prefs.notification_scope}
                  onValueChange={(v) => setPrefs({ ...prefs, notification_scope: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las propiedades</SelectItem>
                    <SelectItem value="properties">Solo propiedades seleccionadas</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Severidad</div>
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(SEV_LABEL).map(([k, l]) => {
                    const active = prefs.notification_severities.includes(k);
                    return (
                      <button
                        key={k}
                        type="button"
                        onClick={() => toggleSev(k)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                          active
                            ? "bg-foreground text-background border-foreground"
                            : "bg-transparent text-muted-foreground border-border/60 hover:border-foreground/40"
                        }`}
                      >{l}</button>
                    );
                  })}
                </div>
              </div>

              {prefs.notification_scope === "properties" && (
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Propiedades incluidas</div>
                  <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto pr-1">
                    {properties.map((p) => {
                      const active = prefs.notification_property_ids.includes(p.id);
                      return (
                        <label
                          key={p.id}
                          className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                            active ? "border-foreground/40 bg-secondary" : "border-border/60 hover:border-foreground/20"
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="accent-foreground"
                            checked={active}
                            onChange={() => togglePropId(p.id)}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm truncate">{p.name}</div>
                            <div className="text-[11px] text-muted-foreground">{p.country} · {p.city || p.address}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving}>{saving ? "Guardando…" : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
